import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { notification } from "antd";
import {
  SendOutlined,
  UserOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import DataProvider from "../../services";
import { useAppStore } from "../../store";
import { convertToLocalFormat } from "../../utils";
import { getAxiosErrorUserMessage } from "../../utils/apiErrorMessage";
import { resetProfileCompletionGate } from "../../lib/profile-completion";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import OnboardingCard, {
  CampaignOnboardingPrompt,
} from "../../components/kobikuul/OnboardingCard";
import type { AuthResponse } from "../../services/auth";
import mcareLogo from "../../assets/images/mcare logo (1) black.png";
import kobikuulAvatar from "../../assets/images/kobikuul.png";

/** Small circular Kobikuul mascot avatar — the source image is full-body,
 *  so crop to the top (face) inside the circle. */
const KobikuulAvatar: React.FC<{ size?: "sm" | "md" }> = ({ size = "sm" }) => (
  <div
    className={`${
      size === "md" ? "w-9 h-9 sm:w-10 sm:h-10" : "w-7 h-7"
    } rounded-full overflow-hidden bg-[#06509E]/10 shrink-0`}
  >
    <img
      src={kobikuulAvatar}
      alt="Kobikuul"
      className="w-full h-full object-cover object-top"
    />
  </div>
);

/** MTN campaign chat — the landing experience for Pulse/Prestige SMS links
 *  (?source=pulse|prestige). Anonymous: guests chat against the monthly
 *  campaign budget, and once it's exhausted the response carries a forced
 *  onboardingPrompt that renders the inline signup card (details are taken
 *  only AFTER the limit — CTO directive 2026-08-14). Strictly a campaign
 *  page: arrivals without a pulse/prestige source are sent home. */

const CAMPAIGN_SOURCES = ["pulse", "prestige"] as const;

const SK = {
  sessionId: "kobikuul_campaign_sessionId",
  messages: "kobikuul_campaign_messages",
  remaining: "kobikuul_campaign_remaining",
  limitReached: "kobikuul_campaign_limitReached",
  trackingId: "kobikuul_campaign_trackingId",
  source: "kobikuul_campaign_source",
  campaign: "kobikuul_campaign_campaign",
} as const;

interface CampaignChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Set when this turn carried the inline onboarding ask. */
  onboardingPrompt?: CampaignOnboardingPrompt;
}

function campaignLabel(source: string | null): string {
  const labels: Record<string, string> = {
    pulse: "Pulse member",
    prestige: "Prestige member",
  };
  return labels[source?.trim().toLowerCase() ?? ""] ?? "Guest";
}

/** Segment greeting first (CEO ask, 2026-08-20): "Akwaaba, Pulse member!"
 *  instead of a generic hello. Matches the backend's "Welcome, Pulse member"
 *  addressing (campaignGuestLabel). */
function welcomeMessageFor(source: string | null): CampaignChatMessage {
  return {
    id: "welcome",
    role: "assistant",
    content: `Akwaaba, ${campaignLabel(source)}! I'm Kobikuul, your AI health assistant. Ask me about symptoms, diet and nutrition, sleep, lab results, mental wellbeing or general health, no sign-up needed to get started. What can I help you with today?`,
  };
}

/** Returning guest whose free answers are spent (products, 2026-08-24): the
 *  standard intro would invite questions they can't ask ("no sign-up needed")
 *  right above a sign-up card — greet the return and hand off to the card. */
function welcomeBackAtLimitMessageFor(
  source: string | null,
): CampaignChatMessage {
  return {
    id: "welcome",
    role: "assistant",
    content: `Akwaaba again, ${campaignLabel(source)}! You've used your free answers for this month — but here's the good news: your free month of MCare is one step away. Claim it below.`,
  };
}

const TYPEWRITER_CHARS_PER_TICK = 2;
const TYPEWRITER_TICK_MS = 24;

function readStoredSource(param: string | null, key: string): string | null {
  if (param?.trim()) {
    try {
      sessionStorage.setItem(key, param.trim());
    } catch {
      /* storage unavailable */
    }
    return param.trim();
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

const KobikuulCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loggedIn, setToken, setUser } = useAppStore();

  // Attribution — URL wins, sessionStorage keeps it across reloads. Also
  // mirrored to the localStorage keys register.tsx already uses, so a
  // visitor who wanders to /sign-up keeps their campaign attribution.
  const [source] = useState<string | null>(() => {
    const s = readStoredSource(searchParams.get("source"), SK.source);
    if (s) {
      try {
        localStorage.setItem("@source", s);
      } catch {
        /* storage unavailable */
      }
    }
    return s;
  });
  const [campaign] = useState<string | null>(() => {
    const c = readStoredSource(searchParams.get("campaign"), SK.campaign);
    if (c) {
      try {
        localStorage.setItem("@campaign", c);
      } catch {
        /* storage unavailable */
      }
    }
    return c;
  });

  const [messages, setMessages] = useState<CampaignChatMessage[]>(() => {
    try {
      const stored = sessionStorage.getItem(SK.messages);
      if (stored) {
        const parsed = JSON.parse(stored) as CampaignChatMessage[];
        if (parsed.length > 0) return parsed;
      }
    } catch {
      /* fall through to welcome */
    }
    return [welcomeMessageFor(source)];
  });
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() => {
    try {
      return sessionStorage.getItem(SK.sessionId) || "";
    } catch {
      return "";
    }
  });
  const [remaining, setRemaining] = useState<number | null>(() => {
    try {
      const stored = sessionStorage.getItem(SK.remaining);
      return stored != null ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  });
  const [limitReached, setLimitReached] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SK.limitReached) === "true";
    } catch {
      return false;
    }
  });
  const [trackingId, setTrackingId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SK.trackingId);
    } catch {
      return null;
    }
  });

  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const [streamingLength, setStreamingLength] = useState(0);
  const streamingTargetRef = useRef(0);
  const streamingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // The site-wide Ebanqo widget covered the send button here and competed
  // with the campaign chat at the conversion moment (product decision
  // 2026-08-20). Hidden for this route only — see the hook.
  useHideEbanqoWidget("kobikuul-campaign");

  // Landing beacon: record the arrival in the attribution funnel BEFORE any
  // message (tap-and-bounce visitors are otherwise invisible in drop-off
  // reporting) and seed the free-answer counter from the real monthly budget
  // instead of waiting for the first reply. Safe on reload — the backend
  // upsert is idempotent per identity.
  const beaconFiredRef = useRef(false);
  useEffect(() => {
    // The ref (not just the cleanup flag) guards React 18 StrictMode's dev
    // double-mount: two concurrent cookie-less beacons would each mint their
    // own identity and fork the funnel row.
    if (!source || beaconFiredRef.current) return;
    beaconFiredRef.current = true;
    // No cancelled-flag here: under StrictMode the cleanup runs between the
    // simulated unmount/remount and would discard the (only) response.
    void (async () => {
      try {
        const { data } = await DataProvider.kobikuulCampaignLanding({
          source,
          ...(campaign && { campaign }),
        });
        setTrackingId(data.trackingId);
        setRemaining(data.remaining);
        if (data.limitReached) setLimitReached(true);
        // Returning at-limit guest (products, 2026-08-21): the tab that
        // showed the signup card is gone with its sessionStorage, but the
        // budget is server-side — the beacon carries the card back, so they
        // never land on a disabled input with nothing to act on.
        if (data.limitReached && data.onboardingPrompt) {
          const cardMessage = {
            id: `onboarding-${Date.now()}`,
            role: "assistant" as const,
            content: "",
            onboardingPrompt: data.onboardingPrompt,
          };
          setMessages((prev) => {
            if (prev.some((m) => m.onboardingPrompt)) return prev;
            // Fresh conversation: swap the standard intro (which invites
            // questions they can no longer ask) for the welcome-back copy.
            if (prev.length === 1 && prev[0].id === "welcome") {
              return [welcomeBackAtLimitMessageFor(source), cardMessage];
            }
            return [...prev, cardMessage];
          });
        }
        try {
          sessionStorage.setItem(SK.trackingId, data.trackingId);
          sessionStorage.setItem(SK.remaining, String(data.remaining));
          if (data.limitReached) {
            sessionStorage.setItem(SK.limitReached, "true");
          }
        } catch {
          /* storage unavailable */
        }
      } catch {
        // Best-effort — chat works without the beacon; the counter appears
        // after the first reply as before.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist chat state
  useEffect(() => {
    if (messages.length === 1 && messages[0].id === "welcome") return;
    try {
      sessionStorage.setItem(SK.messages, JSON.stringify(messages));
    } catch {
      /* storage unavailable */
    }
  }, [messages]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, streamingLength]);

  // Typewriter reveal for assistant replies
  useEffect(() => {
    if (streamingMessageId == null) return;
    const target = streamingTargetRef.current;
    if (target <= 0) {
      setStreamingMessageId(null);
      return;
    }
    streamingIntervalRef.current = setInterval(() => {
      setStreamingLength((prev) => {
        const next = Math.min(prev + TYPEWRITER_CHARS_PER_TICK, target);
        if (next >= target && streamingIntervalRef.current) {
          clearInterval(streamingIntervalRef.current);
          streamingIntervalRef.current = null;
          setStreamingMessageId(null);
        }
        return next;
      });
    }, TYPEWRITER_TICK_MS);
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
      }
    };
  }, [streamingMessageId]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading || limitReached) return;

    const userMessage: CampaignChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const previousMessages = [...messages, userMessage]
        .filter((m) => m.id !== "welcome")
        .slice(-6)
        .map((m) => ({
          role: (m.role === "assistant" ? "system" : "user") as
            | "user"
            | "system",
          content: m.content,
        }));

      const { data } = await DataProvider.kobikuulV2Anonymous({
        message: trimmed,
        ...(sessionId && { sessionId }),
        previousMessages,
        ...(source && { source }),
        ...(campaign && { campaign }),
      });

      if (data.sessionId) {
        setSessionId(data.sessionId);
        try {
          sessionStorage.setItem(SK.sessionId, data.sessionId);
        } catch {
          /* storage unavailable */
        }
      }
      if (data.trackingId) {
        setTrackingId(data.trackingId);
        try {
          sessionStorage.setItem(SK.trackingId, data.trackingId);
        } catch {
          /* storage unavailable */
        }
      }
      if (typeof data.remaining === "number") {
        setRemaining(data.remaining);
        try {
          sessionStorage.setItem(SK.remaining, String(data.remaining));
        } catch {
          /* storage unavailable */
        }
      }
      if (data.limitReached) {
        setLimitReached(true);
        try {
          sessionStorage.setItem(SK.limitReached, "true");
        } catch {
          /* storage unavailable */
        }
      }

      const replyId = (Date.now() + 1).toString();
      setMessages((prev) => [
        ...prev,
        {
          id: replyId,
          role: "assistant",
          content: data.reply,
          ...(data.onboardingPrompt && {
            onboardingPrompt: data.onboardingPrompt,
          }),
        },
      ]);
      streamingTargetRef.current = data.reply.length;
      setStreamingLength(0);
      setStreamingMessageId(replyId);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: getAxiosErrorUserMessage(err),
        },
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [inputValue, isLoading, limitReached, messages, sessionId, source, campaign]);

  // After a successful in-chat signup: establish the session exactly the way
  // register.tsx does, then land on the mcare dashboard. Tracking promotion
  // (signup/subscribed) is handled by the backend via the trackingId sent
  // with signup-verify — nothing to PATCH here.
  const handleOnboardingComplete = useCallback(
    async (
      response: AuthResponse,
      details: { fullname: string; phoneNumber: string },
    ) => {
      await DataProvider.setToken(response.token);
      await DataProvider.setRefreshToken(response.refreshToken);
      setToken({ token: response.token });
      resetProfileCompletionGate();

      const { data: newUsr } = await DataProvider.updateProfile({
        fullname: details.fullname,
        phoneNumber: convertToLocalFormat(details.phoneNumber),
      });
      setUser({ ...newUsr, timenow: new Date() });

      try {
        sessionStorage.removeItem(SK.messages);
        sessionStorage.removeItem(SK.remaining);
        sessionStorage.removeItem(SK.limitReached);
      } catch {
        /* storage unavailable */
      }

      if (response.subscriptionRequest) {
        if (response.subscriptionRequest.error) {
          notification.warning({
            message: "Free month",
            description:
              response.subscriptionRequest.statusMessage ||
              "We couldn't activate your free month automatically. You can subscribe from your dashboard.",
          });
        } else if (response.subscriptionRequest.packageLabel) {
          notification.success({
            message: "You're in!",
            description: `Your free month of ${response.subscriptionRequest.packageLabel} is active. Welcome to mCare.`,
          });
        }
      }
      navigate("/main");
    },
    [navigate, setToken, setUser],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Testing helper, hidden unless REACT_APP_SHOW_DEV_RESET=true (the API also
  // rejects the reset in production). Clears this browser's campaign budget
  // (server-side, cookie identity) and the local chat state — deliberately
  // keeps source/campaign so it simulates a fresh budget, not a fresh arrival.
  const showDevReset = process.env.REACT_APP_SHOW_DEV_RESET === "true";
  const handleDevReset = useCallback(async () => {
    try {
      await DataProvider.resetKobikuulGuestLimits();
    } catch {
      /* reset is best-effort; API blocks it in production */
    }
    setMessages([welcomeMessageFor(source)]);
    setRemaining(null);
    setLimitReached(false);
    setInputValue("");
    setSessionId("");
    try {
      sessionStorage.removeItem(SK.messages);
      sessionStorage.removeItem(SK.remaining);
      sessionStorage.removeItem(SK.limitReached);
      sessionStorage.removeItem(SK.sessionId);
    } catch {
      /* storage unavailable */
    }
    // Re-seed the counter from the fresh budget (same cookie identity).
    if (source) {
      try {
        const { data } = await DataProvider.kobikuulCampaignLanding({
          source,
          ...(campaign && { campaign }),
        });
        setRemaining(data.remaining);
        setTrackingId(data.trackingId);
      } catch {
        /* counter reappears after the first reply instead */
      }
    }
    notification.success({
      message: "Guest limits reset",
      description: "Fresh free-answer budget for this browser.",
    });
  }, [source, campaign]);

  // Signed-in users already have an account — the campaign page is for
  // guests. Send them to the full authenticated chat instead.
  if (loggedIn) {
    return <Navigate to="/main/chat" replace />;
  }
  // Strictly a Pulse/Prestige landing page (CTO): no valid campaign source
  // in the URL or this tab's session → home.
  if (!source || !CAMPAIGN_SOURCES.includes(source.toLowerCase() as any)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="h-[100dvh] flex flex-col bg-neutral-50">
      {/* Header */}
      <header className="shrink-0 bg-white border-b border-neutral-200 px-4 py-2.5 sm:px-5 sm:py-3">
        <div className="flex items-center gap-3 max-w-3xl mx-auto">
          <KobikuulAvatar size="md" />
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-semibold text-neutral-900 m-0 tracking-tight">
              Kobikuul
            </h1>
            <p className="text-xs text-neutral-500 m-0">AI Health Assistant</p>
          </div>
          {showDevReset && (
            <button
              onClick={handleDevReset}
              className="shrink-0 rounded-full border border-neutral-300 px-2.5 py-1 text-xs text-neutral-500 hover:border-[#06509E] hover:text-[#06509E] transition-colors"
              title="Testing only: reset this browser's free-answer budget"
            >
              Reset limits
            </button>
          )}
          <img src={mcareLogo} alt="mCare" className="h-6 sm:h-7 w-auto" />
        </div>
      </header>

      {/* Free-preview / remaining counter */}
      <div className="shrink-0 bg-[#06509E]/5 border-b border-[#06509E]/10 px-4 py-1.5 text-center">
        <span className="text-xs text-[#06509E] font-medium">
          {limitReached
            ? "Free answers used — create your free account below to keep going"
            : remaining != null
              ? `${remaining} free answer${remaining !== 1 ? "s" : ""} remaining this month`
              : "Free preview — no sign-up needed"}
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4 max-w-3xl mx-auto w-full">
        {messages.map((message) => (
          <div key={message.id}>
            <div
              className={`flex items-end gap-2 ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.role === "assistant" && message.content && (
                <KobikuulAvatar />
              )}
              {message.content && (
              <div
                className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                  message.role === "user"
                    ? "bg-[#06509E] text-white rounded-br-md"
                    : "bg-white text-neutral-800 shadow-sm border border-neutral-100 rounded-bl-md"
                }`}
              >
                {message.role === "assistant" &&
                message.id === streamingMessageId
                  ? message.content.slice(0, streamingLength)
                  : message.content}
              </div>
              )}
              {message.role === "user" && (
                <div className="w-7 h-7 rounded-full bg-[#06509E] text-white flex items-center justify-center shrink-0">
                  <UserOutlined className="text-xs" />
                </div>
              )}
            </div>

            {/* Inline signup card, rendered right after the message that
                carried it (forced at the free-limit). */}
            {message.onboardingPrompt && (
              <div className="flex items-end gap-2 justify-start mt-2">
                <KobikuulAvatar />
                <OnboardingCard
                  prompt={message.onboardingPrompt}
                  trackingId={trackingId ?? undefined}
                  source={source}
                  onComplete={handleOnboardingComplete}
                />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex items-end gap-2 justify-start">
            <KobikuulAvatar />
            <div className="bg-white shadow-sm border border-neutral-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-1.5">
                {[0, 150, 300].map((delay) => (
                  <span
                    key={delay}
                    className="w-2 h-2 bg-[#06509E]/40 rounded-full animate-bounce"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-neutral-200 bg-white px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-end gap-2 max-w-3xl mx-auto">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              limitReached
                ? "Free answers used — continue above to unlock more"
                : "Ask a health question..."
            }
            disabled={isLoading || limitReached}
            rows={1}
            maxLength={2000}
            // `enterKeyHint` is a valid HTML attribute but is absent from the
            // @types/react version the deploy toolchain resolves (it's present
            // locally at 18.3.3), which breaks CRA's production type-check.
            // Spread it via the real attributes type so the build stays green
            // on any @types/react, while preserving the mobile "send" key hint.
            {...({
              enterKeyHint: "send",
            } as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            autoCapitalize="sentences"
            // 16px on phones is not a style choice: iOS Safari zooms the page
            // in on any focused field smaller than that, and never zooms back
            // out, so the whole chat is left oversized after the first tap.
            // The 14px design is kept from sm: up, where no zoom happens.
            className="flex-1 resize-none rounded-2xl border border-neutral-300 bg-white px-4 py-2 text-base sm:text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#06509E] focus:border-[#06509E] disabled:opacity-50"
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || limitReached || !inputValue.trim()}
            className="rounded-full bg-[#06509E] hover:bg-[#053d7a] text-white shrink-0 h-9 w-9 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Send"
          >
            {isLoading ? <LoadingOutlined /> : <SendOutlined />}
          </button>
        </div>
        <p className="text-[10px] text-neutral-400 text-center mt-2 m-0">
          Kobikuul provides general health information only. Always consult a
          healthcare professional for medical advice.
        </p>
      </div>
    </div>
  );
};

export default KobikuulCampaign;
